/**
 * NAV ICON — the rail's icon renderer, lifted verbatim out of
 * pages/UserDashboard.js (DP56 shell pass B) where it had been the only copy.
 * Nothing was added, removed or renamed. AppShell is now the only caller.
 */
import React from 'react';

export const navIdToAssignmentKey = {
  'mydesk': 'nav_dashboard',
  'projects': 'nav_projects',
  'tasks': 'nav_tasks',
  'calendar': 'nav_calendar',
  'timetracking': 'nav_timetracking',
  'notes': 'nav_notes',
  'worktable': 'nav_worktable',
  'pipeline': 'nav_leads',
  'clips': 'nav_clips',
  'contacts': 'nav_contacts',
  'contractors': 'nav_contractors',
  'documents': 'nav_documents',
  'survey-templates': 'nav_survey_templates',
  'blueprints': 'nav_blueprints',
  'library': 'nav_library',
  'financials': 'nav_accounting',
  'team': 'nav_team',
  'company-settings': 'nav_settings',
  'admin-settings': 'nav_admin'
};

// Render navigation icon with dark mode and active state support
// Behavior: Grey when inactive, Brand color when active (matches default Lucide icons)
export const renderNavIcon = (item, iconAssignments, API, darkMode, isActive, globalSettings = {}) => {
  const assignmentKey = navIdToAssignmentKey[item.id] || `nav_${item.id}`;
  const assignment = iconAssignments[assignmentKey];
  
  if (assignment && (assignment.url || assignment.icon_url)) {
    let iconUrl = (assignment.url || assignment.icon_url);
    if (iconUrl.startsWith('/api/')) {
      iconUrl = iconUrl.substring(4);
    }
    const fullUrl = iconUrl.startsWith('http') ? iconUrl : `${API}${iconUrl}`;
    const activeColor = globalSettings.active_color || '#BC5D41';
    
    // ACTIVE STATE - Show icon with brand color
    if (isActive) {
      // For SVG icons, use mask technique for proper coloring
      const isSvg = iconUrl.endsWith('.svg') || assignment.type === 'svg';
      
      if (isSvg) {
        return (
          <div 
            className="w-5 h-5 flex-shrink-0"
            style={{
              backgroundColor: activeColor,
              maskImage: `url(${fullUrl})`,
              WebkitMaskImage: `url(${fullUrl})`,
              maskSize: 'contain',
              WebkitMaskSize: 'contain',
              maskRepeat: 'no-repeat',
              WebkitMaskRepeat: 'no-repeat',
              maskPosition: 'center',
              WebkitMaskPosition: 'center'
            }}
          />
        );
      }
      
      // For PNG/other icons, use filter approach
      return (
        <img 
          src={fullUrl} 
          alt={item.label} 
          className="w-5 h-5 flex-shrink-0 object-contain"
          style={{ 
            filter: 'sepia(100%) saturate(300%) brightness(70%) hue-rotate(340deg)'
          }}
        />
      );
    }
    
    // INACTIVE STATE
    if (darkMode) {
      // Dark mode inactive: Use dark variant if available
      if (assignment.dark_url) {
        let darkUrl = assignment.dark_url;
        if (darkUrl.startsWith('/api/')) {
          darkUrl = darkUrl.substring(4);
        }
        const fullDarkUrl = darkUrl.startsWith('http') ? darkUrl : `${API}${darkUrl}`;
        return (
          <img 
            src={fullDarkUrl} 
            alt={item.label} 
            className="w-5 h-5 flex-shrink-0 object-contain opacity-60"
          />
        );
      }
      
      // Dark mode inactive: Show white/light grey muted icon
      const isSvg = iconUrl.endsWith('.svg') || assignment.type === 'svg';
      if (isSvg) {
        return (
          <div 
            className="w-5 h-5 flex-shrink-0"
            style={{
              backgroundColor: '#8A8A8A', // Muted grey
              maskImage: `url(${fullUrl})`,
              WebkitMaskImage: `url(${fullUrl})`,
              maskSize: 'contain',
              WebkitMaskSize: 'contain',
              maskRepeat: 'no-repeat',
              WebkitMaskRepeat: 'no-repeat',
              maskPosition: 'center',
              WebkitMaskPosition: 'center'
            }}
          />
        );
      }
      
      // PNG in dark mode - invert and reduce
      return (
        <img 
          src={fullUrl} 
          alt={item.label} 
          className="w-5 h-5 flex-shrink-0 object-contain"
          style={{ filter: 'brightness(0) invert(1) opacity(0.5)' }}
        />
      );
    }
    
    // Light mode inactive: Grayscale and reduced opacity
    return (
      <img 
        src={fullUrl} 
        alt={item.label} 
        className="w-5 h-5 flex-shrink-0 object-contain"
        style={{ filter: 'grayscale(100%) opacity(0.5)' }}
      />
    );
  }
  
  // Fall back to default Lucide icon
  const IconComponent = item.icon;
  return <IconComponent className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />;
};
